import React, { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import axios from 'axios';
import { uniqBy } from 'lodash';

import { ICareersBlockFields } from '../../../types/contentful';

import { DepartmentBlock } from './DepartmentBlock';
import { Dropdown } from './Dropdown';
import { Text } from '../Text';
import { BlockContainer, Layout, Column, BoxColumn } from '../Layout';

import { CareerBlockIntroText, DropdownWrapper } from './styles';

import {
  MasonryWrapper,
  MASONRY_CLASS,
  MASONRY_COL_CLASS
} from '../MasonryWrapper';

export interface Department {
  title: string;
  postings: AvailableJob[];
  key: string;
}

interface AvailableJob {
  hostedUrl: string;
  text: string;
  key: string;
}

interface Filters {
  department: string | null;
  location: string | null;
}

export function CareersBlock({
  supportingCopy,
  headline
}: ICareersBlockFields) {
  const [allOpenings, setAllOpenings] = useState<Department[]>([]);
  const [openings, setOpenings] = useState<Department[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    department: null,
    location: null
  });

  const breakpointColumnsObj = {
    default: 2,
    767: 1
  };

  // Get the list of jobs from Lever.co API
  useEffect(() => {
    const LEVER_API_URL =
      'https://api.lever.co/v0/postings/greenlight?&group=team&mode=json';

    const fetchData = async () => {
      const result = await axios.get(LEVER_API_URL);

      let allPostings: any = [];
      const openDepartments: Department[] = [];

      result.data.forEach((returnedDepartment: Department) => {
        allPostings = [...allPostings, ...returnedDepartment.postings];
        openDepartments.push(returnedDepartment);
      });

      setAllOpenings(openDepartments);
      setOpenings(openDepartments);
      setDepartments(openDepartments.map(({ title }: Department) => title));
      setLocations(
        uniqBy(allPostings, 'categories.location').map(
          ({ categories }: any) => categories.location
        )
      );
    };

    fetchData();
  }, []);

  useEffect(() => {
    const { department, location } = filters;

    let filteredJobs: Department[] = JSON.parse(JSON.stringify(allOpenings));

    if (department) {
      filteredJobs = filteredJobs.filter(
        ({ title }: Department) => title === department
      );
    }

    if (location) {
      filteredJobs = filteredJobs.reduce(
        (prev: Department[], curr: Department) =>
          prev.concat({
            ...curr,
            postings: curr.postings.filter(
              ({ categories }: any) => categories.location === location
            )
          }),
        []
      );
    }

    setOpenings(
      filteredJobs.filter(({ postings }: Department) => postings.length)
    );
  }, [filters.department, filters.location]);

  const handleFilterSelection = (selection: string | null, filter: string) => {
    setFilters((state) => ({ ...state, [filter]: selection }));
  };

  return (
    <div id="careersDropdown">
      <BlockContainer
        marginBottom={{ mobile: 'm', tablet: 'm', desktop: 'xl' }}
        marginTop={{
          0: 'blockMarginY',
          tablet: 'blockMarginY_tablet',
          desktop: 'xl'
        }}
        borderRadius={{ 0: 'block_desktop' }}
      >
        <Layout>
          <Column size={{ 0: 10 }} flexDirection="column">
            <CareerBlockIntroText id="careersDropdown">
              <Text
                as="h2"
                fontWeight={{ 0: 'h2' }}
                fontSize={{ 0: 'h2_tablet', desktop: '4xl' }}
                marginBottom="m"
              >
                {headline}
              </Text>
              <Text as="p" fontSize={{ 0: 'm' }}>
                {supportingCopy}
              </Text>
            </CareerBlockIntroText>
          </Column>
        </Layout>
      </BlockContainer>
      <BlockContainer
        marginTop={{
          0: 'l',
          tablet: 'blockMarginY_tablet',
          desktop: 'xs'
        }}
        marginBottom={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
      >
        <Layout>
          <Column
            size={{ 0: 12, desktop: 10 }}
            justifyContent="space-around"
            style={{ position: 'relative' }}
          >
            <DropdownWrapper id="careersDropdown" justifyContent="center">
              <Dropdown
                defaultSelection="All departments"
                filter="department"
                dropdownText={filters.department}
                dropdownOptions={departments}
                handleSelection={handleFilterSelection}
                data-testid="department-dropdown"
              />
              <Dropdown
                defaultSelection="All locations"
                filter="location"
                dropdownText={filters.location}
                dropdownOptions={locations}
                handleSelection={handleFilterSelection}
                data-testid="location-dropdown"
              />
            </DropdownWrapper>
          </Column>
          <BoxColumn size={{ 0: 12, desktop: 10 }} marginTop={{ 0: '2xl' }}>
            <MasonryWrapper>
              {openings.length ? (
                <Masonry
                  breakpointCols={breakpointColumnsObj}
                  className={MASONRY_CLASS}
                  columnClassName={MASONRY_COL_CLASS}
                >
                  {openings.map((department: Department) => (
                    <DepartmentBlock
                      key={department.title}
                      title={department.title}
                      availableJobs={department.postings}
                    />
                  ))}
                </Masonry>
              ) : (
                <Text textAlign="center" margin="ml" colour="socialIcon">
                  No job postings match these filters.
                </Text>
              )}
            </MasonryWrapper>
          </BoxColumn>
        </Layout>
      </BlockContainer>
    </div>
  );
}
